---
title: "Trendlines, Channels and Market Structure (HH/HL/LH/LL)"
slug: trendlines-channels-market-structure
shortTitle: "Trendlines & Structure"
description: "Read a trend through its swing highs and lows, spot when market structure breaks, draw trendlines and channels that hold up, and use them to plan entries and stops."
level: intermediate
category: trading
course: intermediate
order: 2
topics: [technical-analysis, chart-patterns]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trendlines-channels-market-structure.svg
faqs:
  - q: "What do HH, HL, LH and LL stand for?"
    a: "Higher high, higher low, lower high and lower low. They describe how each swing compares with the one before it. Higher highs with higher lows describe an uptrend; lower highs with lower lows describe a downtrend."
  - q: "How many touches make a valid trendline?"
    a: "Two swing points are enough to draw a line, but many traders wait for a third touch that holds before they rely on it. A line that only fits if you ignore several candles is probably not a useful one."
  - q: "Should I draw trendlines on the wicks or the candle bodies?"
    a: "Either can work. Wicks capture the true extremes, while bodies ignore brief spikes. What matters is choosing one method and using it consistently on the same chart."
  - q: "Does a broken trendline mean the trend has reversed?"
    a: "Not necessarily. A trendline break often means the trend is slowing or turning sideways. A lower high followed by a break below the last higher low is stronger evidence that an uptrend's structure has ended, though even that does not predict what comes next."
  - q: "Can I draw trendlines on the trade chart?"
    a: "The trade chart does not include drawing tools. You can read exact swing highs and lows from the OHLC legend, work out where a line sits, and set price alerts there, or draw lines in a separate charting tool and place orders on the platform."
---

Before you ask where to enter a trade, it helps to ask a simpler question: which way is this market moving, if any way at all? **Market structure** answers it by looking at the sequence of swing highs and swing lows. **Trendlines** and **channels** then turn that structure into lines you can plan around.

This lesson shows you how to read structure using four labels, HH, HL, LH and LL, how to spot the moment a trend's structure breaks, how to draw trendlines and channels that hold up, and how to turn them into entries and stop losses with a defined risk in USDT.

<CTA label="Open the ETH/USDT chart" to="/trade/ETH" />

<KeyTakeaways>

- **Structure defines the trend.** Higher highs and higher lows describe an uptrend; lower highs and lower lows describe a downtrend; anything else is a range.
- **The last swing point is the line in the sand.** In an uptrend, a break below the most recent higher low is the first clear sign that structure is changing.
- **Two points draw a trendline; a third makes it useful.** Draw lines consistently and never force them to fit.
- **Channels frame the swings.** A parallel line through the opposite swings maps where pullbacks and pushes have tended to end.

</KeyTakeaways>

## Swing highs and swing lows

A **swing high** is a peak: a candle whose high is above the highs of the candles on either side. A **swing low** is the mirror image, a trough. A simple, consistent rule is to require at least two lower highs on each side of a swing high, and two higher lows on each side of a swing low.

Swings depend on the timeframe. A clear swing on a 1h chart can be a blip on the 1D chart, so always say which timeframe you are reading. The OHLC legend on the {{BRAND}} chart gives you the exact high and low of any candle you point at.

## Reading market structure

Compare each swing with the one before it and you get four labels: **higher high (HH)**, **higher low (HL)**, **lower high (LH)** and **lower low (LL)**.

<CompareTable caption="The four structures and what they describe">

| Structure | Swing highs | Swing lows | What it describes |
| --- | --- | --- | --- |
| Uptrend | Higher highs | Higher lows | Buyers stepping in at higher prices |
| Downtrend | Lower highs | Lower lows | Sellers stepping in at lower prices |
| Range | Roughly equal | Roughly equal | Balance between buyers and sellers |
| Squeeze | Lower highs | Higher lows | Swings narrowing, often seen as a triangle |

</CompareTable>

Narrowing swings are the basis of several patterns covered in [Chart Patterns](/academy/article/chart-patterns).

## When structure breaks

An uptrend stays intact while each pullback ends above the previous low. The moment the price breaks below the most recent higher low, the pattern that defined the trend is gone.

<Example title="An uptrend's structure breaking on ETH (illustrative prices, 4h chart)">

The swings run: low **2,800** → high **3,050** → low **2,920** (HL) → high **3,180** (HH) → low **3,010** (HL) → high **3,120** → then a fall below 3,010 to **2,950**.

- While the lows rose (2,800, then 2,920, then 3,010), the uptrend held.
- The high at 3,120 failed to beat 3,180: a **lower high**, the first warning.
- The fall below 3,010 broke the last higher low and made a **lower low**. The uptrend's structure has ended.

</Example>

Some traders call the first break against a trend a **change of character** and a break in the trend's own direction a **break of structure**. The names vary; the idea does not. A break tells you the old pattern has ended. It does not tell you what comes next, which might be a downtrend or simply a range.

## Drawing trendlines

A **trendline** joins swing points to show the slope of a trend. In an uptrend, draw it under the price through two or more swing lows. In a downtrend, draw it above the price through swing highs. A few rules keep lines honest:

- **Wait for a third touch.** Two points always make a line; a third reaction that holds suggests other traders see it too.
- **Be consistent.** Use wicks or bodies, not whichever fits better today.
- **Do not force it.** If a line has to cut through several candles, it is not describing the market.
- **Mind the slope.** Very steep lines tend to break sooner, often into a slower trend rather than a reversal.
- **Use log scale for long periods.** Over large price ranges, a line on a log scale can look different from the same line on a normal scale. The chart settings offer both.

Because a rising trendline climbs every day, its value is a moving target.

<Example title="Projecting a trendline (illustrative prices)">

ETH/USDT makes swing lows at **2,900** on day 1 and **3,000** on day 6.

- Slope: (3,000 − 2,900) ÷ 5 days = **20 USDT a day**.
- Projected value on day 11: 3,000 + 5 × 20 = **3,100**.

</Example>

## Channels

Draw a line parallel to the trendline through the swing highs and you have a **channel**: rising, falling or flat. Price often swings between the two lines until it breaks out.

Continuing the example, the swing high between the two lows was **3,150** on day 4. The trendline's value that day was 2,900 + 3 × 20 = 2,960, so the channel is 3,150 − 2,960 = **190** wide. On day 11 the upper line sits near 3,100 + 190 = **3,290**.

## Using structure in a trade plan

Structure tells you which direction to favor; the trendline and channel suggest where to act; the last swing point tells you where the idea is wrong.

<Example title="A trade at the lower channel line (illustrative prices)">

On day 11, ETH pulls back towards the trendline at about 3,100. The last higher low is **3,050**, and your risk budget is **35 USDT**.

- **Entry:** 3,110. **Stop loss:** 3,040, just below the last higher low. Risk: 3,110 − 3,040 = **70** per ETH.
- **Size:** 35 ÷ 70 = **0.5 ETH**, an Amount of 0.5 × 3,110 = **1,555 USDT**.
- **Take profit:** 3,280, just under the upper line. Reward: 170 × 0.5 = **85 USDT**, about 2.4 times the risk, before costs.

</Example>

As new higher lows form, some traders raise their stop loss under each one. {{BRAND}} has no automatic trailing stop, so you do this yourself with **Edit TP / SL**, or by dragging the SL line on the desktop chart. [Scaling In/Out, Trailing Stops and Partial Take-Profit](/academy/article/scaling-trailing-stops-partial-take-profit) covers the technique.

{/* TODO: verify with platform — automatic trailing stops are not currently offered. */}

<Warning>

Trendlines are subjective, and two traders can draw different lines on the same chart. Breaks are often sharp, and a stop loss fills at market once triggered, so it can slip in a fast move or a gap. Do not trade every touch: combine lines with structure and levels, and size each position to a loss you can afford.

</Warning>

## Working with lines on {{BRAND}}

The trade chart does not currently offer drawing tools, so pair it with a few habits:

- Read swing highs and lows from the OHLC legend and write them down.
- Work out where your trendline sits each day, as in the example, and set a price alert there. Update it as the line climbs or falls.
- If you prefer to see lines drawn, use a separate charting tool for analysis and place your orders on {{BRAND}}.
- For a second opinion on direction, the Markets page shows moving-average-cross readings in its scanner and quick view.

{/* TODO: verify with platform — no drawing tools or indicators on the trade chart; neutral wording about a separate charting tool. */}

<ProTip>

Work top-down. Define the structure on the 1D or 4h chart first, then draw your trendline on the timeframe you trade. A buy setup on the 15m chart carries more weight when the 4h structure is also making higher highs and higher lows. [Multi-Timeframe Analysis](/academy/article/multi-timeframe-analysis) explains how.

</ProTip>

Trendlines work best alongside horizontal levels from [Support and Resistance](/academy/article/support-and-resistance).

## Conclusion

Market structure reduces a busy chart to one question: are the swings making higher highs and higher lows, lower highs and lower lows, or neither? The last swing point marks where the current structure fails. Trendlines and channels turn that structure into lines you can project forward, as long as you draw them consistently and let them earn trust with a third touch. Use them to choose a direction, an area to act and a place for your stop loss, then size the trade to a loss chosen in advance. They describe the market; they do not predict it.
